import React from "react";
import { getDownloadLink } from "@utils/url";

interface ReasonsListProps {
  title: string;
  reasons: string[];
  imageSrc: string;
  imageAlt: string;
  buttonText: string;
}

/**
 * Reasons List component
 *
 * Generic reasons list component that displays a list of reasons with an accompanying image.
 * Receives all content as props for A/B testing flexibility.
 *
 * @param {string} title - Section title
 * @param {string[]} reasons - Array of reason strings
 * @param {string} imageSrc - Path to the image
 * @param {string} imageAlt - Alt text for the image
 * @param {string} buttonText - Text for the call-to-action button
 * @returns {JSX.Element} The reasons list component
 */
export function ReasonsList({
  title,
  reasons,
  imageSrc,
  imageAlt,
  buttonText,
}: ReasonsListProps) {
  const primaryCtaHref = getDownloadLink();

  return (
    <section className="w-full max-w-6xl py-20 px-10 flex flex-col md:flex-row items-center gap-16 justify-around">
      <img src={imageSrc} alt={imageAlt} className="w-80" />

      <div>
        <h2 className="text-3xl font-bold text-slate-800 max-w-md">{title}</h2>
        <ul className="mt-6 space-y-4 text-slate-700 text-lg">
          {reasons.map((reason, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <img src="/images/check-circle.svg" alt="Check mark" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>

        <a href={primaryCtaHref} className="inline-block">
          <button className="mt-8 px-6 py-3 bg-violet-500 text-white rounded-full text-lg">
            {buttonText}
          </button>
        </a>
      </div>
    </section>
  );
}
